import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import axios from 'axios'
import { Loader2 } from 'lucide-react'
import DocumentPreview from '@/components/serge-workspace/DocumentPreview'
import { Button } from '@/components/serge'
import { runSergeAnalysis, getSergeProjectContext } from '@/services/sergeApi'

interface AnalysisContext {
  status?: string
  summary?: string
  entities?: any[]
  constraints?: any[]
}

export default function SergeAnalysePage() {
  const { projectId } = useParams()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [analysis, setAnalysis] = useState<AnalysisContext | null>(null)
  const [extractedContent, setExtractedContent] = useState<string | null>(null)

  useEffect(() => {
    if (projectId) {
      loadAnalysis()
    }
  }, [projectId])

  const loadAnalysis = async () => {
    try {
      setIsLoading(true)
      const context = await getSergeProjectContext(Number(projectId))
      setAnalysis(context.context?.analysis || null)
      setExtractedContent(context.context?.import?.extractedContent || null)
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'analyse')
    } finally {
      setIsLoading(false)
    }
  }

  const handleRunAnalysis = async () => {
    try {
      setIsAnalyzing(true)
      const result = await runSergeAnalysis(Number(projectId))
      setAnalysis(result.analysis || result)
      toast.success('Analyse terminee avec succes')
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? (error.response?.data as { error?: string })?.error || 'Erreur lors de l\'analyse'
        : 'Erreur lors de l\'analyse'
      toast.error(message)
    } finally {
      setIsAnalyzing(false)
    }
  }

  const handleGoToClarification = () => {
    navigate(`/dashboard/serge-projects/${projectId}/clarification`)
  }

  const previewLines = extractedContent
    ? extractedContent.split('\n').slice(0, 10).map((text, i) => ({
        text: text || ' ',
        tone: i === 0 ? 'highlight' as const : 'muted' as const,
      }))
    : [{ text: 'Aucun contenu extrait', tone: 'muted' as const }]

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  const isAnalysisComplete = analysis?.status === 'completed'
  const progress = isAnalysisComplete ? 100 : 0

  return (
    <div className="grid grid-cols-12 gap-4">
      <section className="col-span-12 xl:col-span-7 space-y-4">
        <div className="border border-border bg-surface p-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {isAnalysisComplete ? 'Analyse terminee' : 'Analyse du document'}
              </h2>
              <p className="text-sm text-foreground-secondary mt-2">
                {isAnalyzing
                  ? 'Analyse en cours...'
                  : isAnalysisComplete
                  ? 'L\'analyse IA est complete.'
                  : 'Lancez l\'analyse IA pour extraire les informations cles.'}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs font-mono text-foreground-muted">Progress</p>
              <p className="text-2xl font-semibold text-primary">{progress}%</p>
            </div>
          </div>

          {isAnalyzing && (
            <div className="mt-6 border border-border bg-bg p-4">
              <div className="flex items-center justify-between text-xs font-mono text-foreground-muted">
                <span>Analyse IA en cours</span>
                <span>EN COURS</span>
              </div>
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            {!isAnalysisComplete && (
              <Button
                variant="primary"
                size="md"
                onClick={handleRunAnalysis}
                disabled={isAnalyzing}
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyse en cours...
                  </>
                ) : (
                  'Lancer l\'analyse'
                )}
              </Button>
            )}
            {isAnalysisComplete && (
              <Button variant="primary" size="md" onClick={handleGoToClarification}>
                Passer a la clarification
              </Button>
            )}
            <Button variant="outline" size="md" onClick={loadAnalysis}>
              Rafraichir
            </Button>
          </div>
        </div>

        <DocumentPreview title="Contenu extrait" lines={previewLines} />
      </section>

      <section className="col-span-12 xl:col-span-5 space-y-4">
        <div className="border border-border bg-surface p-5">
          <h3 className="text-sm font-mono text-foreground-muted uppercase tracking-widest">
            Resume auto
          </h3>
          <p className="text-sm text-foreground-secondary mt-3">
            {analysis?.summary || 'L\'analyse n\'a pas encore ete lancee.'}
          </p>
        </div>

        {analysis?.keyPoints && analysis.keyPoints.length > 0 && (
          <div className="border border-border bg-surface p-5">
            <h3 className="text-sm font-mono text-foreground-muted uppercase tracking-widest">
              Points cles identifies
            </h3>
            <ul className="mt-4 space-y-2">
              {analysis.keyPoints.map((point, index) => (
                <li key={index} className="text-sm text-foreground-secondary flex items-start gap-2">
                  <span className="text-primary">•</span>
                  {point}
                </li>
              ))}
            </ul>
          </div>
        )}

        {analysis?.questions && analysis.questions.length > 0 && (
          <div className="border border-border bg-surface p-5">
            <h3 className="text-sm font-mono text-foreground-muted uppercase tracking-widest">
              Questions a clarifier
            </h3>
            <p className="text-xs text-foreground-muted mt-2">
              {analysis.questions.length} question(s) identifiee(s)
            </p>
            <ul className="mt-4 space-y-3 text-sm text-foreground-secondary">
              {analysis.questions.slice(0, 3).map((q) => (
                <li key={q.id} className="border border-border bg-bg p-3">
                  <span className="text-xs text-primary font-mono">{q.priority}</span>
                  <span className="ml-2">{q.title}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </div>
  )
}
